import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowRight, FileText } from "lucide-react";
import { searchPrograms, fetchMeta } from "../lib/api";

const TYPE_LABEL = {
    kommune: "Bergen kommune",
    fylkesting: "Vestland fylkesting",
    nasjonal: "Nasjonalt program (Stortinget)",
};

function highlight(text, queryTokens) {
    if (!text || !queryTokens?.length) return text;
    const re = new RegExp(
        `(${queryTokens.map((t) => t.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")).join("|")})`,
        "gi",
    );
    return text.split(re).map((part, i) => {
        const key = `${i}-${part}`;
        return re.test(part) ? (
            <mark key={key} className="pp-mark">
                {part}
            </mark>
        ) : (
            <span key={key}>{part}</span>
        );
    });
}

export default function Search() {
    const [params, setParams] = useSearchParams();
    const initialQ = params.get("q") || "";
    const [q, setQ] = useState(initialQ);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({ periods: [], types: [] });
    const [filters, setFilters] = useState({
        period: params.get("period") || "",
        type: params.get("type") || "",
    });

    useEffect(() => {
        fetchMeta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        const query = params.get("q");
        if (!query) {
            setResults([]);
            return;
        }
        setLoading(true);
        const reqParams = { q: query };
        ["period", "type"].forEach((k) => {
            const v = params.get(k);
            if (v) reqParams[k] = v;
        });
        searchPrograms(reqParams)
            .then((d) => setResults(d.results || []))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [params]);

    const submit = (e) => {
        e.preventDefault();
        const next = new URLSearchParams();
        if (q.trim()) next.set("q", q.trim());
        Object.entries(filters).forEach(([k, v]) => v && next.set(k, v));
        setParams(next);
    };

    const queryTokens = (params.get("q") || "")
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 1);

    return (
        <main data-testid="search-page" className="mx-auto max-w-7xl px-6 py-12 md:px-12">
            <div className="pp-overline mb-3">Søk i arkivet</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter text-zinc-950 md:text-5xl">
                Fritekstsøk
            </h1>
            <p className="mt-3 max-w-2xl text-zinc-600">
                Søk i alle Rødt-programmer for Bergen, Vestland og Stortinget
                — både gjeldende og historiske. Trykk på et treff for å hoppe
                rett til avsnittet.
            </p>

            <form
                onSubmit={submit}
                data-testid="search-form"
                className="mt-8 flex flex-col gap-3 md:flex-row md:items-stretch"
            >
                <div className="relative flex-1">
                    <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="search-input"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Søk f.eks. «bybane», «boligpolitikk»…"
                        className="pp-search-input pl-12"
                    />
                </div>
                <button
                    data-testid="search-submit"
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 border-2 border-zinc-950 bg-zinc-950 px-6 py-4 font-medium text-white hover:bg-zinc-800"
                    style={{ boxShadow: "4px 4px 0 0 rgba(9,9,11,1)" }}
                >
                    Søk <ArrowRight className="h-4 w-4" />
                </button>
            </form>

            <div className="mt-6 flex flex-wrap items-end gap-4">
                <Filter
                    label="Periode"
                    testid="search-filter-period"
                    value={filters.period}
                    options={meta.periods}
                    onChange={(v) => setFilters({ ...filters, period: v })}
                />
                <Filter
                    label="Nivå"
                    testid="search-filter-type"
                    value={filters.type}
                    options={meta.types}
                    labelMap={TYPE_LABEL}
                    onChange={(v) => setFilters({ ...filters, type: v })}
                />
            </div>

            <section className="mt-12">
                {loading ? (
                    <div data-testid="search-loading" className="font-mono text-sm text-zinc-500">
                        Søker…
                    </div>
                ) : !params.get("q") ? (
                    <div className="text-zinc-500">Skriv inn et søkeord for å starte.</div>
                ) : results.length === 0 ? (
                    <div data-testid="search-no-results" className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center">
                        <h3 className="font-heading text-2xl font-bold text-zinc-950">
                            Ingen treff
                        </h3>
                        <p className="mt-2 text-sm text-zinc-600">
                            Prøv andre ord eller fjern filtre.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="pp-overline mb-4">
                            {results.length} treff
                        </div>
                        <ul className="space-y-4">
                            {results.map((r, idx) => {
                                const linkParams = new URLSearchParams();
                                linkParams.set("q", params.get("q") || "");
                                ["period", "type"].forEach((k) => {
                                    const v = params.get(k);
                                    if (v) linkParams.set(k, v);
                                });
                                linkParams.set("i", String(idx));
                                const linkTo = `/programs/${r.program_id}?${linkParams.toString()}#p-${r.paragraph_id}`;
                                return (
                                    <li
                                        key={r.paragraph_id}
                                        data-testid={`result-${r.paragraph_id}`}
                                        className="border border-zinc-200 bg-white p-5 transition-colors hover:border-zinc-400"
                                    >
                                        <div className="pp-overline mb-2 flex flex-wrap items-center gap-2">
                                            <span className="font-bold text-zinc-700">
                                                {TYPE_LABEL[r.type] || r.type}
                                            </span>
                                            <span>·</span>
                                            <span>{r.period}</span>
                                            <span>·</span>
                                            <span>s. {r.page}</span>
                                        </div>
                                        <p className="leading-relaxed text-zinc-800">
                                            {highlight(r.snippet, queryTokens)}
                                        </p>
                                        <Link
                                            to={linkTo}
                                            data-testid={`result-open-${r.paragraph_id}`}
                                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#C8102E] hover:underline"
                                        >
                                            <FileText className="h-4 w-4" />
                                            Åpne avsnittet i programmet →
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </>
                )}
            </section>
        </main>
    );
}

function Filter({ label, value, options, onChange, testid, labelMap }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="pp-overline">{label}</span>
            <select
                data-testid={testid}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="min-w-[160px] border border-zinc-300 bg-white px-3 py-2 font-sans text-sm text-zinc-900 focus:border-klein focus:outline-none focus:ring-1 focus:ring-klein"
            >
                <option value="">Alle</option>
                {(options || []).map((o) => (
                    <option key={o} value={o}>
                        {labelMap ? labelMap[o] || o : o}
                    </option>
                ))}
            </select>
        </label>
    );
}
